import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Article,
  articleToPuckMetadata,
  findArticleById,
  getPayloadFromPostContentResponse,
  getPostsFromResponse,
  mapPostToArticle,
  preparePuckPageContent,
  type RawPuckComponent
} from '@helpers/Puckhelper';

import { useNavigationStore } from '@vb-django/components/minskog/store/NavigationStore';

import { fetchPostContentById, fetchPosts } from '@services/posts';

import { cn } from '@utils/cn';
import { TailwindWrapper } from '@utils/TailwindWrapper';

import { TwButton as Button, Icon, TwTabs as Tabs, Text } from '@ui-component-library';

import { PuckPostRenderer } from '@components/PuckPostRenderer/PuckPostRenderer';
import { SkeletonLoader } from '@components/PropertySelector/components/SkeletonLoaders';

import { useScreenSize } from '../../vb-django/components/minskog/hooks/useScreenSize';

const TABS = ['Allt', 'Nyheter', 'Artiklar'];
const PAGE_SIZE = 10;
const SUBTITLE = 'Läs om de senaste händelserna';

export const MarketInformation = () => {
  const { isLarge } = useScreenSize();
  const { view } = useNavigationStore();
  const initialPostId = view?.comp === 'marknadsinformation' ? view?.postId : null;
  const [activeTab, setActiveTab] = useState('Allt');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rawPosts, setRawPosts] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [puckComponents, setPuckComponents] = useState<RawPuckComponent[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const articleScrollRef = useRef<HTMLDivElement>(null);
  const currentSelectedIdRef = useRef<string | null>(null);
  const hasUsedInitialPostRef = useRef(false);

  const articles = useMemo(() => rawPosts.map((p) => mapPostToArticle(p)), [rawPosts]);

  const loadPostContent = useCallback(async (postId: string, posts: Article[]) => {
    setLoadingContent(true);
    try {
      const resp = await fetchPostContentById(postId);
      if (currentSelectedIdRef.current !== postId) {
        return;
      }
      if (!resp.ok) {
        console.error('Failed to fetch post content:', resp);
        setPuckComponents([]);
        return;
      }
      const payload = getPayloadFromPostContentResponse(resp);
      setPuckComponents(
        preparePuckPageContent(payload, articleToPuckMetadata(findArticleById(posts, postId)))
      );
    } finally {
      if (currentSelectedIdRef.current === postId) {
        setLoadingContent(false);
      }
    }
  }, []);

  const fetchCategory = useCallback(
    async (category: string, overridePostId?: string | null) => {
      setLoading(true);
      const resp = await fetchPosts({
        category,
        pagesize: PAGE_SIZE,
        pageNr: 1
      });
      const fromApi = getPostsFromResponse(resp);
      const posts = [...fromApi].sort(
        (a, b) =>
          new Date(b.published_date ?? 0).getTime() - new Date(a.published_date ?? 0).getTime()
      );
      setRawPosts(posts);
      setPage(1);
      setHasMore(fromApi.length >= PAGE_SIZE);

      const postIdStr =
        overridePostId !== null && overridePostId !== undefined ? String(overridePostId) : null;
      const targetId = postIdStr ?? (posts.length > 0 ? String(posts[0].id) : null);
      const shouldPreselect = isLarge;
      setSelectedId(shouldPreselect ? targetId : null);
      currentSelectedIdRef.current = shouldPreselect ? targetId : null;

      if (shouldPreselect && targetId) {
        await loadPostContent(targetId, posts);
      } else {
        setPuckComponents([]);
      }
      setLoading(false);
    },
    [isLarge, loadPostContent]
  );

  function tabToApiCategory(tab: string) {
    if (tab === 'Nyheter') {
      return 'Marknadsinformation';
    }
    return tab;
  }

  /* Fetches the next page of posts, appends them to the list, and re-sorts by date */
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) {
      return;
    }
    setLoadingMore(true);
    const nextPage = page + 1;
    const resp = await fetchPosts({
      category: tabToApiCategory(activeTab),
      pagesize: PAGE_SIZE,
      pageNr: nextPage
    });
    const fromApi = getPostsFromResponse(resp);
    setRawPosts((prev) =>
      [...prev, ...fromApi].sort(
        (a, b) =>
          new Date(b.published_date ?? 0).getTime() - new Date(a.published_date ?? 0).getTime()
      )
    );
    setPage(nextPage);
    setHasMore(fromApi.length >= PAGE_SIZE);
    setLoadingMore(false);
  }, [activeTab, hasMore, loadingMore, page]);

  const scrollArticleAreaToTop = () => {
    articleScrollRef.current?.scrollTo(0, 0);
  };

  useEffect(() => {
    const overridePostId = !hasUsedInitialPostRef.current && initialPostId ? initialPostId : null;
    fetchCategory(tabToApiCategory(activeTab), overridePostId);
    if (initialPostId) {
      hasUsedInitialPostRef.current = true;
    }
  }, [activeTab, fetchCategory, initialPostId]);

  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue);
    setSelectedId(null);
    currentSelectedIdRef.current = null;
    setPuckComponents([]);
    setLoadingContent(false);
  };

  const handlePostClick = async (postId: string) => {
    setSelectedId(postId);
    currentSelectedIdRef.current = postId;
    scrollArticleAreaToTop();
    await loadPostContent(postId, rawPosts);
  };

  return (
    <div
      className={`flex h-full grow overflow-visible ${isLarge ? 'flex-col' : ''}`}
      style={{ minHeight: 0 }}
    >
      {isLarge && <div style={{ height: 12, flexShrink: 0 }} aria-hidden />}
      <div
        className={`flex h-full grow overflow-hidden ${isLarge ? 'flex-row' : 'flex-col'}`}
        style={{
          minHeight: 0
        }}
      >
        {/* Left panel  */}
        <div
          className={`${isLarge || !selectedId ? 'flex flex-col' : 'hidden'} h-full overflow-hidden ${isLarge ? 'shrink-0' : 'grow'}`}
          style={
            isLarge ? { width: 380, minWidth: 0, minHeight: 0 } : { minWidth: 0, minHeight: 0 }
          }
        >
          <TailwindWrapper>
            <div className="shrink-0 px-4 pt-5 pb-3 sm:px-5">
              <div className="flex flex-col gap-0.5">
                <Text variant="text-base/leading-normal/medium">Marknadsinformation</Text>
                <Text variant="text-xs/leading-normal/medium" className="text-[#737373]">
                  {SUBTITLE}
                </Text>
              </div>
              <div className="mt-3 flex gap-1">
                <Tabs
                  value={activeTab}
                  tabs={TABS.map((t) => ({ label: t, value: t }))}
                  onChange={handleTabChange}
                />
              </div>
            </div>
          </TailwindWrapper>
          <div className="grow overflow-auto" style={{ minHeight: 0 }}>
            <TailwindWrapper>
              <div>
                {loading ? (
                  <div className="p-4">
                    <SkeletonLoader length={10} type="property" />
                  </div>
                ) : (
                  <>
                    {articles.map((article) => (
                      <Button
                        variant="tertiary"
                        key={article.id}
                        onClick={() => handlePostClick(article.id)}
                        className={cn(
                          'flex w-full flex-row items-start rounded-none border-x-0 border-t-0 border-b border-solid border-[#E6E6E6] text-left hover:bg-[#F5F5F5]',
                          selectedId === article.id && 'bg-[#F5F5F5]'
                        )}
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-1">
                          <Text
                            variant="text-2xs/leading-normal/normal"
                            className="text-xs text-[#737373]"
                          >
                            {article.date} · {article.category}
                            {article.source ? ` · ${article.source}` : ''}
                          </Text>
                          <Text variant="text-sm/leading-normal/normal" className="text-[#0F0F0F]">
                            {article.title}
                          </Text>
                          <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                            {article.summary}
                          </Text>
                          <div className="flex justify-between">
                            <Text
                              variant="text-xs/leading-normal/normal"
                              className="text-[#737373]"
                            >
                              {article.author}
                            </Text>
                            <Icon name="ChevronDownSimple" size="medium" className="rotate-270" />
                          </div>
                        </div>
                      </Button>
                    ))}
                    {hasMore && (
                      <div className="items-center p-4">
                        <Button
                          variant="secondary"
                          className="w-full"
                          disabled={loadingMore}
                          onClick={loadMore}
                        >
                          {loadingMore ? 'Laddar...' : 'Ladda fler'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TailwindWrapper>
          </div>
        </div>
        {isLarge && (
          <div style={{ width: 1, flexShrink: 0, backgroundColor: '#E6E6E6' }} aria-hidden />
        )}

        {/* Right panel — Puck content renders in light DOM (like PuckPageView) */}
        <div
          className={`${isLarge || selectedId ? 'flex flex-col' : 'hidden'} h-full grow overflow-hidden`}
          style={{ minWidth: 0, minHeight: 0, paddingRight: 8, paddingBottom: 12 }}
        >
          {!isLarge && selectedId && (
            <TailwindWrapper>
              <div className="flex shrink-0 justify-end border-b border-[#E6E6E6] px-2 py-1">
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setSelectedId(null);
                    currentSelectedIdRef.current = null;
                  }}
                >
                  <Icon name="CrossSimple" size="large" />
                </Button>
              </div>
            </TailwindWrapper>
          )}
          <div ref={articleScrollRef} className="grow overflow-auto" style={{ minHeight: 0 }}>
            {loading || !selectedId || loadingContent ? (
              <TailwindWrapper>
                <div className="w-full px-4 py-8 sm:px-8">
                  <SkeletonLoader length={8} type="property" />
                </div>
              </TailwindWrapper>
            ) : (
              <PuckPostRenderer components={puckComponents} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
